import type { ActionsUnion} from '@rxsv/core';
import { U, createReducer } from '@rxsv/core';
import type { ChartState } from '@bayer-int/portletce-aclibv2';
import type { Signal } from '@bayer-int/portletce-shared';

import type { Specification, Config, ViewId } from 'portlet/models';
import { AsyncData } from 'portlet/common/models/asyncData';
import type { AppError } from 'portlet/common/errors';
import type { PortletMetadata } from 'portlet/models/portletMetadata';

import type { AppSelector } from '../selectors';

type InitializedState = {
    config: Config;
    spec: Specification;
    metadata: PortletMetadata | null;
    resolvedSignals?: Signal[];
};

export type PortletState = AsyncData<InitializedState, AppError>;
type PortletInitData = {
    config: Config;
    additionalAttrs: Record<string, string>;
    spec?: unknown; // expected to be Specification, but since it comes from the user we cannot trust
    metadata?: PortletMetadata;
};

const initialState: PortletState = AsyncData.create();

export interface ReinitializePersistingOptions {
    persistFilters: boolean;
    persistSignals: boolean | string[];
}

export type ChartRenderPayload = { viewId: ViewId };

export const Action = U.createUnion(
    U.caseOf('PORTLET_INIT')<PortletInitData>(),
    U.caseOf('PORTLET_RESET')(),
    U.caseOf('PORTLET_INIT_SUCCESS')<InitializedState>(),
    U.caseOf('PORTLET_INIT_ERROR')<AppError>(),
    U.caseOf('PORTLET_VISUALIZATION_STATE_CHANGE')<ChartState>(),
    U.caseOf('CHART_RENDERED')<ChartRenderPayload>(),
    U.caseOf('CHART_RENDER_ERROR')<AppError>(),
    U.caseOf('EXTERNAL_REINITIALIZATION')<ReinitializePersistingOptions>(),
);

export type Action = ActionsUnion<typeof Action>;
export const ErrorActions = [Action.PORTLET_INIT_ERROR, Action.CHART_RENDER_ERROR];
export type State = typeof initialState;

export const reducer = createReducer(initialState)<Action>({
    PORTLET_RESET: () => initialState,
    PORTLET_INIT: () => AsyncData.request(),
    PORTLET_INIT_SUCCESS: (_, { payload }) => AsyncData.load(payload),
    PORTLET_INIT_ERROR: (_, { payload }) => AsyncData.error(payload),
    PORTLET_VISUALIZATION_STATE_CHANGE: (state, { payload }) =>
        AsyncData.map(state, data => ({ ...data, resolvedSignals: payload.input.signals })),
    CHART_RENDERED: state => state,
    CHART_RENDER_ERROR: state => state,
    EXTERNAL_REINITIALIZATION: state => state,
});

const specificationSelector: AppSelector<Specification | null> = ({ portlet }) =>
    AsyncData.is.LOADED(portlet) ? portlet.data.spec : null;

const configSelector: AppSelector<Config | null> = ({ portlet }) =>
    AsyncData.is.LOADED(portlet) ? portlet.data.config : null;

const initErrorSelector: AppSelector<AppError | null> = ({ portlet }) =>
    AsyncData.is.ERROR(portlet) ? portlet.error : null;

const metadataSelector: AppSelector<PortletMetadata | null> = ({ portlet }) =>
    AsyncData.is.LOADED(portlet) ? portlet.data.metadata : null;

const resolvedSignalsSelector: AppSelector<Signal[]> = ({ portlet }) =>
    AsyncData.is.LOADED(portlet) || AsyncData.is.RELOADING(portlet)
        ? portlet.data.resolvedSignals || []
        : [];

export const Selectors = {
    specificationSelector,
    configSelector,
    initErrorSelector,
    metadataSelector,
    resolvedSignalsSelector,
};
